import React, { useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  LinearProgress,
  Tooltip,
  Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useDispatch, useSelector } from 'react-redux';
import {
  bundleErrorSelector,
  bundleHistoryErrorSelector,
  bundleHistoryLastFetchedSelector,
  bundleHistoryLoadingSelector,
  bundleHistorySelector,
  bundleLastFetchedSelector,
  bundleLoadingSelector,
  bundleStatusSelector,
  fetchBundleHistory,
  fetchBundleStatus
} from '../../redux/features/bundle/bundleSlice';
import { APP_PATH_PACKAGE_PLAN } from '../../config';
import BundleStatusBadge from '../../components/BundleStatusBadge/BundleStatusBadge';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
    maxWidth: 1200,
    margin: '0 auto'
  },
  hero: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(3),
    padding: theme.spacing(3, 4),
    borderRadius: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    background:
      'linear-gradient(135deg, #fff7ed 0%, #e0f2fe 52%, #f8fafc 100%)',
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      padding: theme.spacing(3)
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: -80,
      right: -120,
      width: 220,
      height: 220,
      background:
        'radial-gradient(circle, rgba(56, 189, 248, 0.18), transparent 70%)',
      borderRadius: '50%'
    }
  },
  heroTitle: {
    fontWeight: 700,
    fontSize: '2.1rem',
    marginBottom: theme.spacing(1)
  },
  heroSubtitle: {
    color: theme.palette.text.secondary,
    maxWidth: 520
  },
  heroActions: {
    display: 'flex',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap'
  },
  heroButton: {
    textTransform: 'none',
    fontWeight: 600
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap'
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: '1.25rem'
  },
  sectionSubtitle: {
    fontSize: '0.9rem',
    color: theme.palette.text.secondary
  },
  creditCard: {
    borderRadius: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[1],
    height: '100%'
  },
  creditHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  creditBadge: {
    marginTop: theme.spacing(2)
  },
  creditMeta: {
    marginTop: theme.spacing(2),
    fontSize: '0.8rem',
    color: theme.palette.text.secondary
  },
  creditProgress: {
    marginTop: theme.spacing(2),
    height: 6,
    borderRadius: 999
  },
  planListCard: {
    borderRadius: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none'
  },
  planRow: {
    padding: theme.spacing(1.5, 0),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1)
  },
  planRowHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap'
  },
  planName: {
    fontWeight: 600,
    fontSize: '0.95rem',
    color: theme.palette.text.primary
  },
  rowMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    flexWrap: 'wrap'
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(6),
    borderRadius: theme.spacing(2),
    border: `1px dashed ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper
  },
  emptyStateTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1)
  },
  emptyStateSubtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3)
  },
  loadingState: {
    textAlign: 'center',
    padding: theme.spacing(6),
    color: theme.palette.text.secondary
  }
}));

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const getPlanTimestamp = (plan) => {
  const candidates = [
    plan?.createdAt,
    plan?.created_at,
    plan?.purchasedAt,
    plan?.startDate,
    plan?.start_date
  ];
  const value = candidates.find((item) => item);
  if (!value) return 0;
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const PlansView = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  // Bundle status selectors
  const bundleStatus = useSelector(bundleStatusSelector);
  const bundleLoading = useSelector(bundleLoadingSelector);
  const bundleError = useSelector(bundleErrorSelector);
  const loadingStatusCredits = useSelector(bundleLoadingSelector);
  const lastFetched = useSelector(bundleLastFetchedSelector);
  const bundleHistory = useSelector(bundleHistorySelector);
  const bundleHistoryLoading = useSelector(bundleHistoryLoadingSelector);
  const bundleHistoryError = useSelector(bundleHistoryErrorSelector);
  const bundleHistoryLastFetched = useSelector(
    bundleHistoryLastFetchedSelector
  );

  const bundleHistoryEntries = useMemo(() => {
    return [...(bundleHistory || [])].sort(
      (a, b) => getPlanTimestamp(b) - getPlanTimestamp(a)
    );
  }, [bundleHistory]);

  const creditsSummary = useMemo(() => {
    if (bundleHistoryEntries.length > 0) {
      const totals = bundleHistoryEntries.reduce(
        (acc, entry) => {
          acc.totalCredits += entry.totalCredits || 0;
          acc.remainingCredits += entry.remainingCredits || 0;
          const entryTimestamp = getPlanTimestamp(entry);
          if (entryTimestamp > acc.latestTimestamp) {
            acc.latestTimestamp = entryTimestamp;
            acc.latestCreatedAt = entry.createdAt;
          }
          return acc;
        },
        {
          totalCredits: 0,
          remainingCredits: 0,
          latestTimestamp: 0,
          latestCreatedAt: null
        }
      );

      return {
        hasActiveBundle: totals.remainingCredits > 0,
        planName: null,
        totalCredits: totals.totalCredits,
        remainingCredits: totals.remainingCredits,
        createdAt: totals.latestCreatedAt
      };
    }

    return bundleStatus;
  }, [bundleHistoryEntries, bundleStatus]);

  const creditsUpdatedAt = creditsSummary?.createdAt || lastFetched;
  const remainingCredits = creditsSummary?.remainingCredits || 0;
  const totalCredits = creditsSummary?.totalCredits || 0;
  const creditsProgress =
    totalCredits > 0 ? (remainingCredits / totalCredits) * 100 : 0;
  const lowCredits = remainingCredits <= 2;

  // Fetch bundle status on mount
  useEffect(() => {
    if (!loadingStatusCredits && !lastFetched) {
      console.log('💳 [DashboardView] Fetching bundle status for user.');
      dispatch(fetchBundleStatus());
    }
  }, [dispatch, loadingStatusCredits, lastFetched]);

  useEffect(() => {
    if (!bundleHistoryLoading && !bundleHistoryLastFetched) {
      console.log('💳 [PlansView] Fetching bundle history for user.');
      dispatch(fetchBundleHistory());
    }
  }, [dispatch, bundleHistoryLoading, bundleHistoryLastFetched]);

  return (
    <div className={classes.root}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Box
            className={classes.hero}
            style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <div>
              <Typography variant="h2" className={classes.heroTitle}>
                Packs & Add-ons
              </Typography>
              <Typography variant="body1" className={classes.heroSubtitle}>
                Keep your job packs organized, track job credits, and review the
                activity at a glance.
              </Typography>
            </div>
            <Button
              variant="contained"
              color="primary"
              className={classes.heroButton}
              onClick={() => history.push(`${APP_PATH_PACKAGE_PLAN}?bundle=1`)}>
              Buy Pack
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card className={classes.creditCard}>
            <CardContent>
              <Box className={classes.creditHeader}>
                <div>
                  <Typography variant="h6">Job Credits</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Credits are used to publish job posts.
                  </Typography>
                </div>
              </Box>
              <Box className={classes.creditBadge}>
                <BundleStatusBadge
                  bundleStatus={creditsSummary}
                  loading={bundleLoading}
                  error={bundleError}
                  showPlanName={false}
                  showProgress={false}
                />
              </Box>
              <Tooltip
                title={
                  bundleLoading
                    ? 'Loading credits...'
                    : `${remainingCredits} of ${totalCredits} credits remaining`
                }
                placement="top"
                arrow>
                <Box className={classes.creditProgress}>
                  <LinearProgress
                    variant={bundleLoading ? 'indeterminate' : 'determinate'}
                    value={bundleLoading ? undefined : creditsProgress}
                    className={classes.creditProgress}
                    color={lowCredits ? 'secondary' : 'primary'}
                  />
                </Box>
              </Tooltip>
              <Typography className={classes.creditMeta}>
                Updated {formatDate(creditsUpdatedAt)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {bundleHistoryError && (
        <Box marginTop={3}>
          <Alert severity="error">
            Unable to load pack purchases: {bundleHistoryError}
          </Alert>
        </Box>
      )}

      <Box className={classes.sectionHeader}>
        <div>
          <Typography className={classes.sectionTitle}>
            Pack purchases
          </Typography>
        </div>
        <Typography className={classes.sectionSubtitle}>
          {bundleHistoryEntries.length} purchase
          {bundleHistoryEntries.length === 1 ? '' : 's'}
        </Typography>
      </Box>

      {bundleHistoryLoading && bundleHistoryEntries.length === 0 ? (
        <Box className={classes.loadingState}>Loading pack purchases...</Box>
      ) : bundleHistoryEntries.length === 0 ? (
        <Box className={classes.emptyState}>
          <Typography variant="h6" className={classes.emptyStateTitle}>
            No pack purchases yet
          </Typography>
          <Typography className={classes.emptyStateSubtitle}>
            Buy a hiring profile to add job packs to your account.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            className={classes.heroButton}
            onClick={() => history.push(`${APP_PATH_PACKAGE_PLAN}?bundle=1`)}>
            Explore plans
          </Button>
        </Box>
      ) : (
        <Card className={classes.planListCard}>
          <CardContent>
            {bundleHistoryEntries.map((entry, index) => {
              const createdAt = formatDate(entry.createdAt);
              return (
                <Box key={entry.id || `${entry.planName}-${index}`}>
                  <Box className={classes.planRow}>
                    <Box className={classes.planRowHeader}>
                      <Typography className={classes.planName}>
                        {entry.planName}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {entry.totalCredits || entry.credits} jobs
                      </Typography>
                    </Box>
                    <Typography variant="caption" className={classes.rowMeta}>
                      {createdAt}
                    </Typography>
                  </Box>
                  {index < bundleHistoryEntries.length - 1 && <Divider />}
                </Box>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PlansView;
